import { useMemo } from 'react';
import { format, formatInTimeZone } from 'date-fns-tz';

import { TimezoneTable } from '@components/primitives/Timestamp/timezone-table';
import { Tooltip } from '@components/primitives/Tooltip';

import css from './styles.module.css';

type TooltipContentProps = {
  value: Date | string | number;
  timezone?: string;
  variant?: 'none' | 'simple' | 'detailed';
};

function TooltipContent({ value, timezone, variant }: TooltipContentProps) {
  const tooltipValue = useMemo(() => {
    if (timezone) {
      return formatInTimeZone(value, timezone, 'MMM d, yyyy h:mm:ss a');
    }
    return format(value, 'MMM d, yyyy h:mm:ss a');
  }, [value, timezone]);

  if (variant === 'simple') {
    return <span className={css.simpleTime}>{tooltipValue}</span>;
  }

  if (variant === 'detailed') {
    return <TimezoneTable value={value} />;
  }
}

export type TimestampFormat =
  | string
  | ((value: Date | string | number) => string);

export interface TimestampProps {
  className?: string;
  value: Date | string | number;
  format?: TimestampFormat;
  timezone?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  tooltip?: 'none' | 'simple' | 'detailed';
  delay?: number;
}

export function Timestamp({
  className,
  value,
  format: formatStr = 'MMM d, yyyy h:mm:ss a',
  timezone,
  side = 'top',
  align = 'center',
  tooltip = 'simple',
  delay,
}: TimestampProps) {
  const formattedValue = useMemo(() => {
    if (typeof formatStr === 'function') {
      return formatStr(value);
    }
    if (timezone) {
      return formatInTimeZone(value, timezone, formatStr);
    }
    return format(value, formatStr);
  }, [value, formatStr, timezone]);

  return (
    <Tooltip
      className={css.tooltip}
      align={align}
      side={side}
      delay={delay}
      content={
        tooltip !== 'none' && (
          <TooltipContent value={value} timezone={timezone} variant={tooltip} />
        )
      }
    >
      <time
        className={className}
        dateTime={format(value, 'yyyy-MM-dd HH:mm:ss')}
      >
        {formattedValue}
      </time>
    </Tooltip>
  );
}

export * from './ago';
export * from './timezone-table';
